<!-- ══════════════════════════════════════════════════════════════════════ -->
<!-- Step 1: search / filter / multi-select                                 -->
<!-- ══════════════════════════════════════════════════════════════════════ -->
<app-shared-dialog
  *ngIf="step === 'search'"
  [title]="'knowledge_base.shipping_exclusions.dialog.title' | translate"
  [options]="{
    cancelText: 'common.cancel' | translate,
    customConfirmButton: true,
    size: 'large'
  }"
  (cancel)="close()"
>
  <div class="shipping-exclusion-modal__search-step">
    <p class="shipping-exclusion-modal__description">
      {{ "knowledge_base.shipping_exclusions.dialog.description" | translate }}
    </p>

    <!-- Toolbar: search + filter -->
    <div class="shipping-exclusion-modal__toolbar">
      <ui-form-field
        [formControl]="searchControl"
        [prefixIcon]="'search'"
        [placeholder]="'knowledge_base.shipping_exclusions.dialog.location_placeholder' | translate"
        class="shipping-exclusion-modal__search"
      >
      </ui-form-field>
      <button
        class="action-button"
        (click)="setFilter()"
        [disabled]="!filterOptionsReady"
        [class.action-button--active]="hasActiveFilters()"
        [matTooltip]="!filterOptionsReady ? ('knowledge_base.shipping_exclusions.filter.loading' | translate) : ''"
      >
        <div class="action-button-icon">
          <img src="assets/icones/filter_icon.svg" alt="" />
        </div>
        <span class="action-button-text">{{ "knowledge_base.filter" | translate }}</span>
        <span class="action-button-badge" *ngIf="hasActiveFilters()">{{ activeFilterCount() }}</span>
        <div class="clear-icon-container" *ngIf="hasActiveFilters()">
          <img class="clear-icon" (click)="clearFilter(); $event.stopPropagation()" src="assets/icones/clear-icon.svg" alt="" />
        </div>
      </button>
    </div>

    <!-- Bulk select: everything matching the current search/filter, not just what's paged in -->
    <div class="shipping-exclusion-modal__select-all-bar" *ngIf="!loading && sortedLocations.length > 0">
      <ng-container *ngIf="!selectAllMatchingActive">
        <button
          *ngIf="canSelectAllMatching"
          type="button"
          class="shipping-exclusion-modal__select-all-link"
          [disabled]="isSelectingAll"
          (click)="selectAllMatching()"
        >
          {{ (isSelectingAll ? "knowledge_base.shipping_exclusions.dialog.selecting_all" : "knowledge_base.shipping_exclusions.dialog.select_all") | translate: { count: matchingCount } }}
        </button>
      </ng-container>
      <div class="shipping-exclusion-modal__select-all-active" *ngIf="selectAllMatchingActive">
        <span>{{ "knowledge_base.shipping_exclusions.dialog.all_selected" | translate: { count: selectedCount } }}</span>
        <button type="button" class="shipping-exclusion-modal__select-all-link" (click)="clearSelection()">
          {{ "knowledge_base.shipping_exclusions.dialog.clear_selection" | translate }}
        </button>
      </div>
    </div>

    <!-- Results: checkbox multi-select list -->
    <div class="shipping-exclusion-modal__results">
      <div class="shipping-exclusion-modal__results-header">
        <div class="shipping-exclusion-modal__results-cell shipping-exclusion-modal__results-cell--checkbox" (click)="$event.stopPropagation()">
          <ui-checkbox
            [checked]="isAllVisibleSelected"
            [indeterminate]="isPartialVisibleSelected"
            [ariaLabel]="'common.select_all' | translate"
            (changed)="toggleAllVisible($event.checked)"
          ></ui-checkbox>
        </div>
        <div class="shipping-exclusion-modal__results-cell">{{ "knowledge_base.shipping_exclusions.col.location_name" | translate }}</div>
        <div class="shipping-exclusion-modal__results-cell">{{ "knowledge_base.shipping_exclusions.col.address" | translate }}</div>
        <div class="shipping-exclusion-modal__results-cell">{{ "knowledge_base.shipping_exclusions.col.company_name" | translate }}</div>
        <div class="shipping-exclusion-modal__results-cell">{{ "knowledge_base.shipping_exclusions.col.erp_id" | translate }}</div>
      </div>

      <div class="shipping-exclusion-modal__results-body" (scroll)="onResultsScroll($event)">
        <div class="shipping-exclusion-modal__loading" *ngIf="loading">
          <div class="shipping-exclusion-modal__loading-spinner"></div>
        </div>

        <ng-container *ngIf="!loading && sortedLocations.length > 0">
          <div
            class="shipping-exclusion-modal__results-row"
            *ngFor="let location of sortedLocations"
            (click)="toggleLocation(location, !isSelected(location.id))"
          >
            <div class="shipping-exclusion-modal__results-cell shipping-exclusion-modal__results-cell--checkbox" (click)="$event.stopPropagation()">
              <ui-checkbox
                [checked]="isSelected(location.id)"
                [ariaLabel]="'common.select' | translate"
                (changed)="toggleLocation(location, $event.checked)"
              ></ui-checkbox>
            </div>
            <div class="shipping-exclusion-modal__results-cell"><span class="shipping-exclusion-modal__cell-text">{{ location.name || "--" }}</span></div>
            <div class="shipping-exclusion-modal__results-cell" [matTooltip]="location.full_address || ''"><span class="shipping-exclusion-modal__cell-text">{{ location.full_address || "--" }}</span></div>
            <div class="shipping-exclusion-modal__results-cell" [matTooltip]="location.company_name || ''"><span class="shipping-exclusion-modal__cell-text">{{ location.company_name || "--" }}</span></div>
            <div class="shipping-exclusion-modal__results-cell"><span class="shipping-exclusion-modal__cell-text">{{ location.erp_id || "--" }}</span></div>
          </div>

          <div class="shipping-exclusion-modal__loading-more" *ngIf="isLoadingMore">
            <div class="shipping-exclusion-modal__loading-spinner"></div>
          </div>
        </ng-container>

        <div class="shipping-exclusion-modal__empty" *ngIf="!loading && sortedLocations.length === 0">
          {{ "knowledge_base.shipping_exclusions.dialog.no_locations" | translate }}
        </div>
      </div>
    </div>
  </div>

  <ui-button
    customConfirm
    *appHasPermission="submitPermission()"
    type="primary"
    [label]="(selectedCount > 0 ? 'knowledge_base.shipping_exclusions.dialog.next_with_count' : 'knowledge_base.shipping_exclusions.dialog.next') | translate: { count: selectedCount }"
    icon="arrow-right"
    iconPosition="end"
    [disabled]="!hasSelection"
    (click)="goToPreview()"
  >
  </ui-button>
</app-shared-dialog>

<!-- ══════════════════════════════════════════════════════════════════════ -->
<!-- Step 2: preview / confirm                                               -->
<!-- ══════════════════════════════════════════════════════════════════════ -->
<app-shared-dialog
  *ngIf="step === 'preview'"
  [title]="'knowledge_base.shipping_exclusions.dialog.preview_title' | translate"
  [options]="{
    cancelText: 'common.back' | translate,
    customConfirmButton: true,
    size: 'large'
  }"
  (cancel)="goBackToSearch()"
>
  <div class="shipping-exclusion-modal__preview-step">
    <p class="shipping-exclusion-modal__description">
      {{ "knowledge_base.shipping_exclusions.dialog.preview_description" | translate: { count: selectedCount } }}
    </p>

    <div class="shipping-exclusion-modal__preview-list">
      <div class="shipping-exclusion-modal__preview-row" *ngFor="let location of previewLocations">
        <span class="shipping-exclusion-modal__preview-name">{{ location.name || "--" }}</span>
        <span class="shipping-exclusion-modal__preview-erp">{{ location.erp_id || "--" }}</span>
        <button
          class="shipping-exclusion-modal__preview-remove"
          [matTooltip]="'common.remove' | translate"
          (click)="removeSelected(location.id)"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <p class="shipping-exclusion-modal__preview-more" *ngIf="previewHiddenCount > 0">
        {{ "knowledge_base.shipping_exclusions.dialog.preview_more" | translate: { count: previewHiddenCount } }}
      </p>
    </div>
  </div>

  <ui-button
    customConfirm
    *appHasPermission="submitPermission()"
    type="primary"
    [label]="'knowledge_base.shipping_exclusions.dialog.confirm_add' | translate: { count: selectedCount }"
    icon="arrow-right"
    iconPosition="end"
    [disabled]="!!(submitting$ | async) || !hasSelection"
    (click)="confirm()"
  >
  </ui-button>
</app-shared-dialog>
